import { withAlpha } from "@/lib/color";
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { COMPETITION_META, GROUP_ORDER } from "@/lib/competitions";

// Normalize a name for matching: uppercase, strip apostrophes/spaces/punctuation
function normalizeName(s) {
    if (!s) return "";
    return s
        .toString()
        .toUpperCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip diacritics
        .replace(/[\s'’`.\-_]/g, ""); // strip spaces, apostrophes, dots
}

export default function TrophyCabinet({ teamId, managerName, aliases = [], isAdmin = false, onAliasesUpdated }) {
    const [entries, setEntries] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [editing, setEditing] = useState(aliases || []);
    const [newAlias, setNewAlias] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setEditing(aliases || []);
    }, [aliases]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/albo/entries");
                if (!cancelled) setEntries(data || []);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const trophies = useMemo(() => {
        if (!entries) return [];
        // Match SOLO su manager o alias espliciti. Mai sul nome della squadra:
        // nell'Albo d'Oro un nome-squadra (es. "Chelsea") indica che il manager non era noto.
        const allTargets = [managerName, ...(aliases || [])].map(normalizeName).filter(Boolean);
        if (allTargets.length === 0) return [];
        const set = new Set(allTargets);
        return entries.filter((e) => {
            const winner = normalizeName(e.winner);
            return winner && set.has(winner);
        });
    }, [entries, managerName, aliases]);

    // Trophies flat list, sorted by group order then count desc
    const flatTrophies = useMemo(() => {
        const map = {};
        for (const t of trophies) {
            const key = t.competition_key;
            if (!map[key]) map[key] = { key, seasons: [], count: 0 };
            map[key].seasons.push(t.season);
            map[key].count += 1;
        }
        for (const k of Object.keys(map)) {
            map[k].seasons.sort((a, b) => b - a);
        }
        const items = [];
        for (const k of Object.keys(map)) {
            const meta = COMPETITION_META[k];
            if (!meta) continue;
            items.push({ ...map[k], meta });
        }
        items.sort((a, b) => {
            const ga = GROUP_ORDER.indexOf(a.meta.group);
            const gb = GROUP_ORDER.indexOf(b.meta.group);
            if (ga !== gb) return ga - gb;
            if (b.count !== a.count) return b.count - a.count;
            return a.meta.label.localeCompare(b.meta.label);
        });
        return items;
    }, [trophies]);

    const totalCount = trophies.length;

    // Suggest available albo names (for autocomplete-style hint)
    const knownNames = useMemo(() => {
        if (!entries) return [];
        const set = new Set();
        for (const e of entries) if (e.winner) set.add(e.winner);
        return Array.from(set).sort();
    }, [entries]);

    const addAlias = () => {
        const s = (newAlias || "").trim();
        if (!s) return;
        if (editing.some((x) => x.toUpperCase() === s.toUpperCase())) {
            setNewAlias("");
            return;
        }
        setEditing([...editing, s]);
        setNewAlias("");
    };

    const removeAlias = (idx) => {
        setEditing(editing.filter((_, i) => i !== idx));
    };

    const saveAliases = async () => {
        if (!teamId) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/teams/${teamId}/albo-aliases`, { aliases: editing });
            if (onAliasesUpdated) onAliasesUpdated(data.albo_aliases || []);
            setShowEditor(false);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert(e.response?.data?.detail || e.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <section className="mb-10" data-testid="trophy-cabinet-loading">
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO BACHECA TROFEI…</div>
            </section>
        );
    }
    if (err) return null;

    return (
        <section className="mb-10" data-testid="trophy-cabinet">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
                <div>
                    <div className="op-label" style={{ color: "rgb(var(--tc-ffd700))" }}>// BACHECA DEI TROFEI</div>
                    <h2 className="op-heading text-2xl sm:text-3xl mt-1">
                        {totalCount > 0 ? (
                            <>
                                <span style={{ color: "rgb(var(--tc-ffd700))" }}>{totalCount}</span>{" "}
                                {totalCount === 1 ? "trofeo" : "trofei"} conquistati
                            </>
                        ) : (
                            <span className="text-t8f95a3">Nessun trofeo ancora</span>
                        )}
                    </h2>
                    {aliases && aliases.length > 0 && (
                        <div className="op-mono text-[10px] text-t8f95a3 mt-1 flex items-center gap-1 flex-wrap">
                            <span>Alias Albo:</span>
                            {aliases.map((a, i) => (
                                <span key={i} className="px-1.5 py-0.5 border border-t22252a text-tffffff">{a}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {totalCount === 0 && !isAdmin && (
                        <p className="text-xs text-t8f95a3 max-w-md">
                            Quando il manager vincerà la prima competizione, il trofeo apparirà qui.
                        </p>
                    )}
                    {isAdmin && teamId && (
                        <button
                            type="button"
                            onClick={() => setShowEditor((v) => !v)}
                            className="op-btn op-btn--ghost text-[10px]"
                            data-testid="trophy-alias-toggle"
                        >
                            {showEditor ? "Chiudi" : "Modifica alias albo"}
                        </button>
                    )}
                </div>
            </div>

            {isAdmin && showEditor && (
                <div className="op-card p-4 mb-6" data-testid="trophy-alias-editor">
                    <div className="op-label text-[10px] mb-2" style={{ color: "rgb(var(--tc-2bb6ff))" }}>
                        // ALIAS ALBO D'ORO (admin)
                    </div>
                    <p className="text-xs text-tc9ccd3 mb-3 leading-relaxed">
                        Aggiungi qui i nomi con cui questo manager compare nell'Albo d'Oro storico.
                        Es: il presidente <span className="text-tffffff">Peppe Russiola</span> dell'Inter è
                        registrato come <span className="text-tffffff">RUSSO</span> → aggiungi <span className="op-mono text-tffd700">RUSSO</span> e
                        i trofei storici di "RUSSO" entreranno in questa Bacheca.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {editing.map((a, i) => (
                            <span
                                key={i}
                                className="op-mono text-xs px-2 py-1 border border-tffd700 text-tffd700 flex items-center gap-2 bg-tffd700/5"
                            >
                                {a}
                                <button
                                    type="button"
                                    onClick={() => removeAlias(i)}
                                    className="text-tff4d4d hover:text-tffffff"
                                    aria-label={`Rimuovi ${a}`}
                                    data-testid={`alias-remove-${i}`}
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                        {editing.length === 0 && (
                            <span className="op-mono text-[10px] text-t8f95a3">Nessun alias.</span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <input
                            type="text"
                            value={newAlias}
                            onChange={(e) => setNewAlias(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                            placeholder="Es. RUSSO"
                            list="albo-known-names"
                            className="op-input op-mono text-xs flex-1 min-w-[160px] uppercase"
                            data-testid="alias-input"
                        />
                        <datalist id="albo-known-names">
                            {knownNames.map((n) => <option key={n} value={n} />)}
                        </datalist>
                        <button
                            type="button"
                            onClick={addAlias}
                            className="op-btn op-btn--ghost text-xs"
                            data-testid="alias-add"
                        >
                            + Aggiungi
                        </button>
                        <button
                            type="button"
                            onClick={saveAliases}
                            disabled={saving}
                            className="op-btn text-xs"
                            data-testid="alias-save"
                        >
                            {saving ? "Salvataggio…" : "Salva"}
                        </button>
                    </div>
                </div>
            )}

            {totalCount > 0 && (
                <div className="flex flex-wrap gap-2.5" data-testid="trophy-cabinet-grid">
                    {flatTrophies.map((it) => (
                        <TrophyCard key={it.key} item={it} />
                    ))}
                </div>
            )}
        </section>
    );
}

function TrophyCard({ item }) {
    const { meta, count, seasons } = item;
    return (
        <div
            className="op-card relative overflow-hidden p-0 group transition-all hover:scale-[1.04] hover:border-t2bb6ff w-[124px] sm:w-[140px] md:w-[150px] flex-shrink-0"
            style={{ borderTop: `2px solid ${meta.color}` }}
            data-testid={`trophy-${item.key}`}
        >
            {/* Trophy showcase */}
            <div
                className="relative aspect-square flex items-center justify-center overflow-hidden"
                style={{
                    background: `radial-gradient(ellipse at center, ${withAlpha(meta.color, "33")} 0%, ${withAlpha(meta.color, "08")} 45%, rgb(var(--tc-0a0b0e)) 100%)`,
                }}
            >
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: `radial-gradient(circle at center, ${withAlpha(meta.color, "50")} 0%, transparent 60%)`,
                        filter: "blur(12px)",
                        opacity: 0.5,
                    }}
                />
                {meta.image ? (
                    <img
                        src={meta.image}
                        alt={meta.label}
                        loading="lazy"
                        className="relative z-10 max-h-[82%] max-w-[72%] object-contain drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)] group-hover:scale-105 transition-transform duration-500"
                        style={{ filter: `drop-shadow(0 0 8px ${withAlpha(meta.color, "66")})` }}
                    />
                ) : (
                    <div
                        className="op-heading text-3xl relative z-10 px-2 text-center leading-tight"
                        style={{ color: meta.color, opacity: 0.85 }}
                    >
                        {meta.label.split(" ")[0]}
                    </div>
                )}
                {count > 1 && (
                    <div
                        className="absolute top-1.5 right-1.5 z-20 op-mono text-[10px] font-bold px-1.5 py-0.5 rounded border bg-t0a0b0e/80"
                        style={{ borderColor: meta.color, color: meta.color }}
                    >
                        ×{count}
                    </div>
                )}
            </div>
            <div className="px-2.5 py-2 border-t border-t22252a bg-t0a0b0e">
                <div
                    className="op-mono text-[10px] uppercase leading-tight truncate"
                    style={{ color: meta.color }}
                    title={meta.label}
                >
                    {meta.label}
                </div>
                <div className="op-mono text-[9px] text-t8f95a3 mt-0.5 truncate" title={seasons.map((s) => `S${s}`).join(", ")}>
                    {seasons.slice(0, 4).map((s) => `S${s}`).join(" · ")}
                    {seasons.length > 4 && ` +${seasons.length - 4}`}
                </div>
            </div>
        </div>
    );
}
